import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Hidden from '@mui/material/Hidden';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { ROLE_POWER, ROLE_NAMES } from '../../models/User';
import DrawerListItem from './DrawerListItem';
import SubscriptionStatus from './SubscriptionStatus';
import memberDrawerStyles from '../Styles/MemberDrawer.Style';
import DisplaySupport from '../SupportDialog';

import {
  selectOpenSupportDialog,
  openSupportDialog,
  setOpenSupportDialog
} from '../../reduxStore/modules/drawer';

const DrawerContent = React.memo(({
  userRole,
  onRecharge,
  onUpgrade,
  getListItemStyles,
  onSupportClick,
  isLoading = false
}) => {
  const openSupportDialog = useSelector(selectOpenSupportDialog);
  const dispatch = useDispatch();

  // Track if initial menu load has completed to prevent flashing during background refreshes
  const hasCompletedInitialMenuLoad = useRef(false);
  const cachedUserRole = useRef(null);

  // Mark initial load complete and cache role when we have a valid role (Owner or Staff)
  if (!hasCompletedInitialMenuLoad.current && userRole && (userRole === ROLE_NAMES.OWNER || userRole === ROLE_NAMES.STAFF)) {
    hasCompletedInitialMenuLoad.current = true;
    cachedUserRole.current = userRole;
  }

  // Show menu items after initial load - don't hide during background refreshes
  const shouldShowMenuItems = hasCompletedInitialMenuLoad.current ||
    (!isLoading && userRole);

  // Billing is only visible to Owner - use cached role to prevent flashing during refreshes
  const shouldShowBilling = (hasCompletedInitialMenuLoad.current && cachedUserRole.current === ROLE_NAMES.OWNER)
    || userRole === ROLE_NAMES.OWNER;

  return (
    <Box sx={(theme) => memberDrawerStyles.root(theme)}>
      {/* SubscriptionStatus - always render but handle loading inside */}
      <List
        sx={(theme) => ({
          ...memberDrawerStyles.toolbar(theme),
          ...memberDrawerStyles.toolbarHeight(theme)
        })}
        disablePadding
      >
        <SubscriptionStatus
          onRecharge={onRecharge}
          onUpgrade={onUpgrade}
          isLoading={isLoading}
        />
      </List>

      <List
        sx={(theme) => memberDrawerStyles.grow(theme)}
        disablePadding
      >
        {/* Always show - no role check required */}
        <DrawerListItem to="/member" labelKey="Activity" getListItemStyles={getListItemStyles} />

        {/* Show Tenants and Staff for all authenticated users */}
        {shouldShowMenuItems && (
          <>
            <DrawerListItem to="/customers" labelKey="Tenants" getListItemStyles={getListItemStyles} />
            <DrawerListItem to="/staff" labelKey="Staff" getListItemStyles={getListItemStyles} />
          </>
        )}

        {/* Always show - no role check required */}
        <DrawerListItem to="/neo" labelKey="Neo the Bot" getListItemStyles={getListItemStyles} />
        <DrawerListItem to="/tags" labelKey="Tags" getListItemStyles={getListItemStyles} />
        <DrawerListItem to="/integrations" labelKey="Integrations" getListItemStyles={getListItemStyles} />

        {/* Billing is only visible to Owner */}
        {shouldShowBilling && (
          <DrawerListItem to="/billing" labelKey="Billing" getListItemStyles={getListItemStyles} />
        )}

        {/* Always show - no role check required */}
        {/* <DrawerListItem to="/share" labelKey="Share" getListItemStyles={getListItemStyles} /> */}
      </List>
      
      {/* Support section */}
      <List disablePadding>
        <Tooltip title={'Get support information'}>
          <ListItem
            button
            sx={(theme) => ({
              ...memberDrawerStyles.listItem(theme),
              ...memberDrawerStyles.listItemSub(theme),
              ...memberDrawerStyles.flexContainer(theme)
            })}
          >
            <Box
              component="p"
              sx={(theme) => ({
                ...memberDrawerStyles.menuLink(theme),
                ...memberDrawerStyles.flexItem1(theme)
              })}
              onClick={() => dispatch(setOpenSupportDialog(true))}
            >
              {'Support'}
            </Box>
          </ListItem>
        </Tooltip>
      </List>

      <DisplaySupport
        open={openSupportDialog}
        setOpen={(open) => dispatch(setOpenSupportDialog(open))}
      />
    </Box>
  );
});

DrawerContent.displayName = 'DrawerContent';

export default DrawerContent;